import httpClient from "@/api/config/httpClient";
import { ProductModificationProcess } from "@/api/models/customer";
import { mapShoppingCartConfigurationBundlesToUpdate } from "../adapters/configuration.adapter";
import { ShoppingCartConfigurationBundleDto, ShoppingCartConfigurationDto } from "../models/shoppingCart.model";
import { ShoppingCartAdapter, ShoppingCartBundlesPromotionsAndContractsDto, UpdateShoppingCartBundleContractDto } from "../shopping-cart";
import { ExtendedShoppingCartBundlePromotionsAndContracts, ExtendedShoppingCartBundlesPromotionsAndContracts } from "@/models/shopping-cart.model";

const v1Url = `${process.env.VUE_APP_API_URL}/v1/api`;

export abstract class ProductModificationProcessService {
	private static readonly BASE_URL = "sys03/customer/product";
	private static readonly PRODUCT_MODIFICATION_PROCESS_URL = `/${ProductModificationProcessService.BASE_URL}/modification-process`;
	private static readonly MODIFICATION_PROCESS_URL = "modification-process/shopping-cart";
	private static readonly PROMOTIONS_AND_CONTRACTS_URL = "promotions-and-contracts";
	private static readonly PROMOTIONS_URL = "promotions";
	private static readonly CONTRACTS_URL = "contracts";
	private static readonly CONFIGURATION_URL = "configuration";
	private static readonly SUMMARY_URL = "summary";

	public static create({ productModificationProcess }: { productModificationProcess: ProductModificationProcess }) {
		const { productId, shoppingCartId } = productModificationProcess;

		return httpClient.post(this.PRODUCT_MODIFICATION_PROCESS_URL, {
			productId,
			shoppingCartId
		});
	}

	public static getById(modificationId: string) {
		const url = `${this.PRODUCT_MODIFICATION_PROCESS_URL}/${modificationId}`;

		return httpClient.get(url);
	}

	public static update(modificationId: string, data: any) {
		const url = `${this.PRODUCT_MODIFICATION_PROCESS_URL}/${modificationId}`;
		return httpClient.patch(url, data);
	}

	public static send(modificationId: string) {
		const url = `${this.PRODUCT_MODIFICATION_PROCESS_URL}/${modificationId}/send`;
		return httpClient.put(url);
	}

	public static getModificationProcessPromotionsAndContracts({
		productId,
		shoppingCartId
	}: {
		productId: string;
		shoppingCartId: string;
	}): Promise<ExtendedShoppingCartBundlesPromotionsAndContracts> {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.PROMOTIONS_AND_CONTRACTS_URL}`;
		return httpClient.get<ShoppingCartBundlesPromotionsAndContractsDto>(url).then((response) => {
			const { data } = response;

			return ShoppingCartAdapter.extendShoppingCartBundlesPromotionsAndContractsDto(data);
		});
	}

	public static putModificationProcessPromotions({
		productId,
		shoppingCartId,
		bundles = []
	}: {
		productId: string;
		shoppingCartId: string;
		bundles: ExtendedShoppingCartBundlePromotionsAndContracts[];
	}): Promise<ExtendedShoppingCartBundlesPromotionsAndContracts> {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.PROMOTIONS_URL}`;

		const body = ShoppingCartAdapter.buildUpdateShoppingCartPromotionsDto({
			bundles,
			applyDefaultContract: false
		});

		return httpClient.put<ShoppingCartBundlesPromotionsAndContractsDto>(url, body).then((response) => {
			const { data } = response;

			return ShoppingCartAdapter.extendShoppingCartBundlesPromotionsAndContractsDto(data);
		});
	}

	public static putModificationProcessContracts({
		productId,
		shoppingCartId,
		bundles = []
	}: {
		productId: string;
		shoppingCartId: string;
		bundles: UpdateShoppingCartBundleContractDto[];
	}): Promise<ExtendedShoppingCartBundlesPromotionsAndContracts> {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.CONTRACTS_URL}`;
		return httpClient.put<ShoppingCartBundlesPromotionsAndContractsDto>(url, { bundles }).then((response) => {
			const { data } = response;

			return ShoppingCartAdapter.extendShoppingCartBundlesPromotionsAndContractsDto(data);
		});
	}

	public static getModificationProcessConfiguration({ productId, shoppingCartId }: { productId: string; shoppingCartId: string }) {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.CONFIGURATION_URL}`;
		return httpClient.get<ShoppingCartConfigurationDto>(url);
	}

	public static putModificationProcessConfiguration({
		productId,
		shoppingCartId,
		bundles = []
	}: {
		productId: string;
		shoppingCartId: string;
		bundles: ShoppingCartConfigurationBundleDto[];
	}) {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.CONFIGURATION_URL}`;
		const body = mapShoppingCartConfigurationBundlesToUpdate(bundles);

		return httpClient.put<ShoppingCartConfigurationDto>(url, body);
	}

	public static getModificationProcessSummary({ productId, shoppingCartId }: { productId: string; shoppingCartId: string }) {
		const url = `${v1Url}/${this.BASE_URL}/${productId}/${this.MODIFICATION_PROCESS_URL}/${shoppingCartId}/${this.SUMMARY_URL}`;
		return httpClient.get(url);
	}

	public static delete(modificationId: string) {
		const url = `${this.PRODUCT_MODIFICATION_PROCESS_URL}/${modificationId}`;
		return httpClient.delete(url);
	}
}
